import { Component } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { find, kebabCase } from "lodash"
import { v4 as uuid } from "uuid"
import moment from "moment"
import classNames from "classnames"
import resizable from "hocs/resizable"
import tooltipContainer from "hocs/tooltip-container"
import { DATE, CHART_ITEM, DRIFT_VALUES } from "helpers/shapes"
import { computeDrift } from "helpers/drift"
import Drift from "components/drift/drift"
import Tooltip from "components/charts/tooltip"
import ChartDefs from "./components/chart-defs"
import { HIGHLIGHTER, LINE } from "../shapes"
import Dot from "../dot"
import Axis, { AXIS } from "../helpers/axis"
import { getData, getLine, getZero, getScaleX, getScaleY, getHighlighter, getSelected, getXAxis, getDriftValues } from "./helper"
import "./style.scss"

class AbsoluteLineChart extends Component {
  static propTypes = {
    tooltip: PropTypes.element.isRequired,
    line: LINE.isRequired,
    highlighter: HIGHLIGHTER,
    scaleX: PropTypes.func.isRequired,
    label: PropTypes.string.isRequired,
    scaleY: PropTypes.func.isRequired,
    data: PropTypes.arrayOf(CHART_ITEM).isRequired,
    startDate: DATE,
    endDate: DATE,
    height: PropTypes.number.isRequired,
    width: PropTypes.number,
    resizableRef: PropTypes.func.isRequired,
    zero: PropTypes.number.isRequired,
    subscribe: PropTypes.func.isRequired,
    formatter: PropTypes.func.isRequired,
    selected: PropTypes.object.isRequired,
    unsubscribe: PropTypes.func.isRequired,
    date: DATE,
    setDate: PropTypes.func.isRequired,
    xAxis: AXIS,
    driftValues: DRIFT_VALUES,
  }

  constructor(props) {
    super(props)

    const chartId = uuid()
    this.pathGradientId = `path-gradient-${chartId}`
    this.axisGradientId = `axis-gradient-${chartId}`
    this.valueElement = null
    this.labelElement = null
  }

  valueRef = el => {
    this.valueElement = el
  }

  labelRef = el => {
    this.labelElement = el
  }

  findByOffset = offset => {
    const { data, scaleX } = this.props
    const lookup = scaleX.invert(offset - 2) // -2 is to give a margin of selection on the first element
    return find(data, datum => datum.date >= lookup)
  }

  getDriftIcon = (contributor) => {
    const { driftValues } = this.props
    if (!driftValues || !contributor) {
      return null
    }

    const x = this.getDriftArrowPosition()
    return <Drift drift={computeDrift(driftValues.mean, driftValues.stdDev, contributor.value)} y={-22} x={x} viewBox="" />
  }

  setDate = offset => {
    const { setDate } = this.props
    const { date } = this.findByOffset(offset)
    setDate(moment(date).format("YYYY-MM-DD"))
  }

  handleGraphClick = ev => {
    const box = ev.currentTarget.getBoundingClientRect()
    const offsetX = ev.pageX - box.left
    this.setDate(offsetX)
  }

  findDotCoordinates = offset => {
    const { scaleX, scaleY } = this.props
    const datum = this.findByOffset(offset)

    if (datum) {
      return {
        cx: scaleX(datum.date),
        cy: scaleY(datum.value),
      }
    } else {
      return null
    }
  }

  computeStyles = () => {
    const { line, highlighter, width } = this.props
    const pathStyle = {}
    const axisStyle = {}

    if (line.style.color && (!highlighter || !width)) {
      pathStyle.stroke = line.style.color
    } else if (highlighter && width) {
      pathStyle.stroke = `url(#${this.pathGradientId})`
      axisStyle.stroke = `url(#${this.axisGradientId})`
    }

    return [pathStyle, axisStyle]
  }

  getValuePosition = () => {
    const { highlighter, width } = this.props
    let position = width

    if (highlighter) {
      // Put the `value` text label to the `right` of the `highlighter` by default.
      position = highlighter.x1 + 5

      if (this.valueElement) {
        const valueWidth = this.valueElement.getBoundingClientRect().width
        const labelWidth = this.labelElement.getBoundingClientRect().width

        /**
         * If the space available between the `right` of the chart and the `highlighter`
         * is < the width of the `value` text label, then put the value to the left of the `highlighter`.
         *
         * Otherwise if the space available between the `left` of the chart and the `highlighter`
         * is < the width of the `label` of the chart, then put the value to its `right`.
         */
        if ((width - highlighter.x1) < valueWidth) {
          position = (highlighter.x1 - valueWidth) - 5
        } else if (highlighter.x1 < labelWidth) {
          position = labelWidth + 5
        }
      }
    } else if (this.valueElement) {
      const valueWidth = this.valueElement.getBoundingClientRect().width
      position = width - valueWidth
    }

    return position
  }

  getDriftArrowPosition = () => {
    let driftArrowPosition = this.getValuePosition()
    let valueWidth = 0

    if (this.valueElement) {
      valueWidth = this.valueElement.getBoundingClientRect().width
    }

    driftArrowPosition += (valueWidth - 3)

    return driftArrowPosition
  }

  render() {
    const {
      data,
      resizableRef,
      height,
      label,
      line,
      subscribe,
      unsubscribe,
      highlighter,
      tooltip,
      width,
      selected,
      formatter,
      xAxis,
    } = this.props

    if (!data.length) {
      return <div ref={resizableRef}>No Data</div>
    } else {
      const [pathStyle] = this.computeStyles()
      return <div data-test-class={`${kebabCase(label)}-chart`} className="components_charts_absolute-line-chart">
        <div ref={this.labelRef} className="chart-title">{label}</div>
        <svg ref={resizableRef} onClick={this.handleGraphClick} className="chart" style={{ height }}>
          <ChartDefs pathId={this.pathGradientId} axisId={this.axisGradientId} offset={highlighter && highlighter.x1} chartWidth={width} />
          <g>
            {highlighter && <line className="highlighter" {...highlighter} />}
            <text ref={this.valueRef} y="-5" x={this.getValuePosition()} className="chart-value">
              {formatter(selected.value)}
            </text>
            {this.getDriftIcon(selected)}
          </g>
          <Axis {...xAxis} />
          <path className={classNames("path", { "single-dot": data && data.length === 1 })} {...line} style={pathStyle} />
          <Dot {...{ subscribe, unsubscribe, findDotCoordinates: this.findDotCoordinates }} />
        </svg>
        <Tooltip {...{ subscribe, unsubscribe, findByOffset: this.findByOffset }}>
          {tooltip}
        </Tooltip>
      </div>
    }
  }
}

const mapStateToProps = (state, props) => ({
  data: getData(props),
  line: getLine(props),
  zero: getZero(props),
  scaleX: getScaleX(props),
  scaleY: getScaleY(props),
  xAxis: getXAxis(props),
  highlighter: getHighlighter(props),
  selected: getSelected(props),
  driftValues: getDriftValues(props),
})

export default tooltipContainer(resizable(connect(mapStateToProps)(AbsoluteLineChart)))
